import { Alert } from "@relex-solutions/fragments"
import { memo, useMemo } from "react"
import { useTranslation } from "react-i18next"
import { useParams } from "react-router"
import { useGetModules } from "@/domains/customers/hooks/useGetModules"
import { ErrorComponent } from "@/shared/components/ErrorComponent/ErrorComponent"
import { Loading } from "@/shared/components/Loading/Loading"
import { RESOURCE_ABILITIES } from "@/shared/constants/permissions"
import { useAuthorization } from "@/shared/hooks/useAuthorization"
import { ModuleFamily } from "../ModuleFamily/ModuleFamily"
import styles from "./ModuleCardContent.module.css"
import { getModulesByFamily } from "./ModuleCardContent.utils"

function ModuleCardContent() {
  const { customerId } = useParams<{ customerId: string }>()
  const { t } = useTranslation()
  const { can, isLoading: isAuthLoading } = useAuthorization()

  const canViewModules = useMemo(
    () => Boolean(customerId) && can(RESOURCE_ABILITIES.VIEW_MODULES, `customer:${customerId}`),
    [can, customerId]
  )

  const query = useGetModules(customerId, canViewModules)
  const modulesByFamily = useMemo(() => (query.data ? getModulesByFamily(query.data.data) : {}), [query.data])

  if (isAuthLoading || query.isLoading) {
    return <Loading />
  }

  if (!canViewModules) {
    return (
      <Alert color="info" role="status">
        {t("customer.overview.modulesNoAccess")}
      </Alert>
    )
  }

  if (query.isError || !query.data) {
    return <ErrorComponent error={query.error} onRetry={() => void query.refetch()} hideIconAndTitle />
  }

  if (query.data.data.length === 0) {
    return <span>{t("customer.overview.noModules")}</span>
  }

  return (
    <div className={styles.container}>
      {Object.entries(modulesByFamily).map(([family, modules]) => (
        <ModuleFamily key={family} family={family} modules={modules} />
      ))}
    </div>
  )
}

export default memo(ModuleCardContent)
